import React from "react";
import { EventType, Album, Folder, EnhancedFolder } from "./types";
import EventList from "./EventList";
import {
  findEntityPath,
  getEncodedEntityPath,
  getEntitySegment,
  isFolderSelected,
  getSelectionClass,
  EntityType,
  typeMap
} from "./FolderFeatures/EncryptionOfFolder";

const DownArrow = require("../styles/images/DownArrow.svg");
const SideArrow = require("../styles/images/SideArrow.svg");
const folderIcon = require("../styles/images/folder-icon.svg");
const shortcutIcon = require("../styles/images/shortcut-icon.svg");

interface SidebarTreeItemProps {
  folder: EnhancedFolder;
  onFolderClick: (folder: EnhancedFolder, encodedPath: string) => void;
  onEventClick: (event: EventType, encodedPath: string, open?: boolean) => void;
  albums: Record<number, Album[]>;
  onAlbumClick: (album: Album, encodedPath: string, event: EventType) => void;
  openedEvents: Set<number>;
  openedFolders: Set<string>;
  generateEventInstanceId: (event: EventType, parentPath?: string) => string;
  isEventInstanceExpanded: (eventInstanceId: string) => boolean;
  onEventCollapse: (event: EventType, parentPath?: string) => void;
  onFolderCollapse: (folder: Folder, parentPath?: string) => void;
  getFolderPath: (
    folder: EnhancedFolder,
    rootFolders: EnhancedFolder[]
  ) => string;
  selectedAlbum: Album | null;
  selectedEvent: EventType | null;
  selectedFolderId: number | null;
  albumLoadingEventId: number | null;
  setShowNewAlbumModal: (eventId: number) => void;
  isMobile: boolean;
  jumpToContentView: () => void;
  folderLoadingId: number | null;
  handleURLChange: () => void;
  libraryRootFolders: EnhancedFolder[];
  setEncodedString: (encodedString: string) => void;
  currentEncodedPath: string | null;
  parentPath?: string; // Hierarchical path context for unique folder instance identification
}

const SidebarTreeItem: React.FC<SidebarTreeItemProps> = ({
  folder,
  onFolderClick,
  onEventClick,
  albums,
  onAlbumClick,
  openedEvents,
  openedFolders,
  generateEventInstanceId,
  isEventInstanceExpanded,
  onEventCollapse,
  onFolderCollapse,
  getFolderPath,
  selectedAlbum,
  selectedEvent,
  selectedFolderId,
  albumLoadingEventId,
  setShowNewAlbumModal,
  isMobile,
  jumpToContentView,
  folderLoadingId = null,
  handleURLChange,
  libraryRootFolders,
  setEncodedString,
  currentEncodedPath,
  parentPath = ""
}) => {
  // Build the unique path identifier for this folder instance
  // This ensures each folder instance has a unique identifier regardless of
  // whether it's a regular folder or a shortcut in different tree locations
  const folderSegment =
    folder.isReference && folder.referenceId
      ? `r:${folder.referenceId}`
      : `f:${folder.id}`;

  const currentFolderPath = parentPath
    ? `${parentPath}/${folderSegment}`
    : folderSegment;

  const isFolderOpen = openedFolders.has(currentFolderPath);
  const isFolderInCurrentPath = isFolderSelected(
    folder.id,
    currentEncodedPath,
    folder.isReference,
    folder.referenceId
  );

  const handleFolderToggle = () => {
    onFolderCollapse(folder, parentPath);
  };

  const handleFolderNameClick = () => {
    // Use the current path context to maintain shortcut folder paths
    // Instead of searching from root, build path from current context
    const folderSegment =
      folder.isReference && folder.referenceId
        ? `r:${folder.referenceId}`
        : `f:${folder.id}`;
    const fullPath = parentPath
      ? `${parentPath}/${folderSegment}`
      : folderSegment;
    const encodedPath = getEncodedEntityPath(fullPath);

    if (encodedPath) {
      setEncodedString(encodedPath);
      onFolderClick(folder, encodedPath);
    }
    // Also expand the folder in sidebar to match arrow state
    onFolderCollapse(folder, parentPath);
  };

  const handleEventClickWithNavigation = (event: EventType) => {
    let path = "";

    if (parentPath) {
      const eventSegment = getEntitySegment(event);
      path = `${parentPath}/${eventSegment}`;
    } else {
      const searchId =
        event.isReference && event.referenceId ? event.referenceId : event.id;
      const foundPath = findEntityPath(libraryRootFolders, searchId, "event", !event.isReference);
      if (foundPath) {
        path = foundPath;
      } else {
        path = getEntitySegment(event);
      }
    }

    const encodedPath = getEncodedEntityPath(path);

    if (encodedPath) {
      setEncodedString(encodedPath);
      onEventClick(event, encodedPath);
    }
    // Also expand the event in sidebar to match arrow state
    onEventCollapse(event, parentPath);
  };

  const showLoader =
    folderLoadingId === folder.id &&
    (!folder.children || folder.children.length === 0) &&
    (!folder.events || folder.events.length === 0);

  return (
    <div key={folder.id}>
      <li
        className={`ws-sidebar-container ${
          isFolderOpen ? "open" : ""
        } ${getSelectionClass(isFolderInCurrentPath)}`}
      >
        <div
          className="ws-arrow-spacing"
          onClick={handleFolderToggle}
          style={{
            marginLeft: isFolderOpen ? 0 : 3,
            marginRight: isFolderOpen ? 0 : 2
          }}
        >
          <img src={isFolderOpen ? DownArrow : SideArrow} alt="toggle-folder" />
        </div>
        <span
          className="ws-event-group"
          onClick={() => {
            handleFolderNameClick();
          }}
        >
          <img src={folderIcon} className="ws-file-spacing" />
          {folder.isReference && (
            <img src={shortcutIcon} className="ws-shortcut-icon-sidebar " />
          )}
          <span className="ws-event-name full-width-text">{folder.name}</span>
        </span>
      </li>
      {isFolderOpen && (
        <ul>
          {showLoader ? (
            <li style={{ marginLeft: "28px" }}>
              <span className="fa fa-spin fa-spinner" />
            </li>
          ) : (
            <>
              {Array.isArray(folder.children) && folder.children.map(subFolder => (
                <li
                  key={getFolderPath(subFolder, libraryRootFolders)}
                  className="ws-album-border"
                >
                  <SidebarTreeItem
                    folder={subFolder}
                    onFolderClick={onFolderClick}
                    onEventClick={handleEventClickWithNavigation}
                    albums={albums}
                    onAlbumClick={onAlbumClick}
                    openedEvents={openedEvents}
                    openedFolders={openedFolders}
                    generateEventInstanceId={generateEventInstanceId}
                    isEventInstanceExpanded={isEventInstanceExpanded}
                    onEventCollapse={onEventCollapse}
                    onFolderCollapse={onFolderCollapse}
                    getFolderPath={getFolderPath}
                    selectedAlbum={selectedAlbum}
                    selectedEvent={selectedEvent}
                    selectedFolderId={selectedFolderId}
                    albumLoadingEventId={albumLoadingEventId}
                    setShowNewAlbumModal={setShowNewAlbumModal}
                    isMobile={isMobile}
                    jumpToContentView={jumpToContentView}
                    folderLoadingId={folderLoadingId}
                    handleURLChange={handleURLChange}
                    libraryRootFolders={libraryRootFolders}
                    setEncodedString={setEncodedString}
                    currentEncodedPath={currentEncodedPath}
                    parentPath={currentFolderPath}
                  />
                </li>
              ))}
              {folder.events && folder.events.length > 0 && (
                <EventList
                  events={folder.events}
                  handleEventClick={handleEventClickWithNavigation}
                  albums={albums}
                  openedEvents={openedEvents}
                  generateEventInstanceId={generateEventInstanceId}
                  isEventInstanceExpanded={isEventInstanceExpanded}
                  handleAlbumClick={onAlbumClick}
                  handleEventCollapse={onEventCollapse}
                  selectedAlbum={selectedAlbum}
                  selectedEvent={selectedEvent}
                  jumpToContentView={jumpToContentView}
                  isMobile={isMobile}
                  onFolderCollapse={onFolderCollapse}
                  getFolderPath={getFolderPath}
                  openedFolders={openedFolders}
                  folderLoadingId={folderLoadingId}
                  handleURLChange={handleURLChange}
                  libraryRootFolders={libraryRootFolders}
                  setEncodedString={setEncodedString}
                  // These props are specific to EventList pagination - use defaults for sidebar
                  currentPage={1}
                  handlePageChange={() => {}}
                  nextPage={null}
                  loadMore={() => {}}
                  albumLoadingEventId={albumLoadingEventId}
                  eventsLoading={false}
                  setShowNewAlbumModal={setShowNewAlbumModal}
                  handleFolderClickInSidebar={onFolderClick}
                  rootFolders={folder?.children || []}
                  folderId={folder.id}
                  currentEncodedPath={currentEncodedPath}
                  parentPath={currentFolderPath}
                />
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
};

export default SidebarTreeItem;
